// 📄 pages/SAP-BTP/RepositorioIflows.tsx
// Repositorio de iFlows (SAP BTP · Integration Suite), ver
// docs/plans/plan-repositorio-iflows-v1.md. Muestra los iFlows de la
// ORGANIZACIÓN ACTIVA del selector de la barra (orgActual): la personal
// (flujos propios, de independiente) o una de equipo (flujos de la empresa).
// Cambiar el selector cambia el repositorio — no se pide proyecto.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FolderGit2, Plus, RefreshCw, Search, User, Users, Archive, Download, FileText, ShieldAlert, ArrowRight, Info, KeyRound } from 'lucide-react';
import Breadcrumbs from '../../components/ui/Breadcrumbs.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { ESTADOS_IFLOW, Iflow, MAX_DOCUMENTOS, descargarIflow, listarIflows } from '../../services/iflowService.ts';
import SubirIflowModal from './components/SubirIflowModal.tsx';
import DetalleIflowModal from './components/DetalleIflowModal.tsx';
import DisenosRepositorio from './components/DisenosRepositorio.tsx';
import PerfilesValoresModal from './components/PerfilesValoresModal.tsx';

interface RepositorioIflowsProps {
  setPage: (page: string) => void;
}

const COLOR_ESTADO: Record<string, string> = {
  borrador: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  probado: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  productivo: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
};

const fecha = (ts: any) => (ts?.toDate ? ts.toDate().toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

function IflowCard({ iflow, onDescargar, onAbrir }: { iflow: Iflow; onDescargar: () => Promise<void>; onAbrir: () => void }) {
  const [bajando, setBajando] = useState(false);
  const tipos = [...new Set(iflow.adaptadores.map((a) => a.tipo))];
  const estado = ESTADOS_IFLOW.find((e) => e.value === iflow.estado)?.label || iflow.estado;

  const descargar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBajando(true);
    try {
      await onDescargar();
    } finally {
      setBajando(false);
    }
  };

  return (
    <article
      onClick={onAbrir}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onAbrir(); } }}
      role="button"
      tabIndex={0}
      aria-label={`Ver ${iflow.titulo}`}
      className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col gap-3 hover:border-cyan-500/30 transition-all cursor-pointer text-left"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-white font-semibold truncate" title={iflow.titulo}>{iflow.titulo}</h3>
          <p className="text-xs text-gray-500 font-mono truncate">{iflow.idTecnico} · v{iflow.versionIflow || '—'} · rev. {iflow.versionActual}</p>
        </div>
        <span
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${COLOR_ESTADO[iflow.estado] || COLOR_ESTADO.borrador}`}
          title={iflow.aprobacion ? `${estado} por ${iflow.aprobacion.nombre || 'alguien'} (rev. ${iflow.aprobacion.revision})` : undefined}
        >
          {estado}
        </span>
      </div>

      <p className="text-sm text-gray-400 line-clamp-2">{iflow.descripcion}</p>

      {(iflow.sistemaOrigen || iflow.sistemaDestino) && (
        <p className="text-xs text-gray-300 flex items-center gap-1.5">
          <span>{iflow.sistemaOrigen || '—'}</span>
          <ArrowRight className="w-3 h-3 text-cyan-400" />
          <span>{iflow.sistemaDestino || '—'}</span>
          {iflow.solucion && <span className="text-gray-500">· {iflow.solucion}</span>}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {tipos.map((t) => <span key={t} className="text-[11px] bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 rounded px-2 py-0.5">{t}</span>)}
        {iflow.etiquetas.map((t) => <span key={t} className="text-[11px] bg-white/5 text-gray-300 border border-white/10 rounded px-2 py-0.5">#{t}</span>)}
      </div>

      <footer className="flex items-center justify-between gap-3 pt-2 mt-auto border-t border-white/10 text-xs text-gray-500">
        <div className="flex items-center gap-3 min-w-0">
          <span className="truncate">{iflow.autorNombre}{fecha(iflow.actualizadoEn) && ` · ${fecha(iflow.actualizadoEn)}`}</span>
          {iflow.documentos?.length > 0 && (
            <span className="flex items-center gap-1" title="Documentación adjunta"><FileText className="w-3.5 h-3.5" />{iflow.documentos.length}</span>
          )}
          {iflow.tieneValoresFijos && (
            <span className="flex items-center gap-1 text-amber-400" title="Tiene direcciones o alias fijos dentro del .zip"><ShieldAlert className="w-3.5 h-3.5" /></span>
          )}
        </div>
        <button onClick={descargar} disabled={bajando} className="flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 disabled:opacity-50 whitespace-nowrap">
          {bajando ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} .zip
        </button>
      </footer>
    </article>
  );
}

export default function RepositorioIflows({ setPage }: RepositorioIflowsProps) {
  const { user, orgActual, orgsLoading } = useAuth();
  const [iflows, setIflows] = useState<Iflow[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [abiertoId, setAbiertoId] = useState<string | null>(null);
  // Pestañas: los iFlows o los diseños (bocetos, E5 de plan-visor-diagrama-iflows.md).
  const [pestana, setPestana] = useState<'iflows' | 'disenos'>('iflows');
  const [disenoAbiertoId, setDisenoAbiertoId] = useState<string | null>(null);
  // Perfiles de valores por cliente (docs/plans/plan-perfiles-valores.md).
  const [perfilesAbierto, setPerfilesAbierto] = useState(false);

  const mostrarAviso = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 5000);
  };

  const [texto, setTexto] = useState('');
  const [filtroAdaptador, setFiltroAdaptador] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroSolucion, setFiltroSolucion] = useState('');

  const sinAcceso = orgActual?.role === 'invitado';
  const archivada = orgActual?.archivada === true;

  const cargar = useCallback(async () => {
    if (!orgActual || sinAcceso) {
      setIflows([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    setError(null);
    try {
      setIflows(await listarIflows(orgActual.id));
    } catch (err) {
      console.error('Error cargando iFlows:', err);
      setError('No se pudieron cargar los iFlows de esta organización.');
    } finally {
      setCargando(false);
    }
  }, [orgActual, sinAcceso]);

  useEffect(() => {
    setAbiertoId(null);
    setDisenoAbiertoId(null);
    setTexto('');
    setFiltroAdaptador('');
    setFiltroEstado('');
    setFiltroSolucion('');
    cargar();
  }, [cargar]);

  const adaptadores = useMemo(() => [...new Set(iflows.flatMap((i) => i.adaptadores.map((a) => a.tipo)))].sort(), [iflows]);
  const soluciones = useMemo(() => [...new Set(iflows.map((i) => i.solucion).filter(Boolean))].sort(), [iflows]);

  const filtrados = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return iflows.filter((i) =>
      (!t || [i.titulo, i.descripcion, i.idTecnico, i.sistemaOrigen, i.sistemaDestino, i.solucion, ...i.etiquetas].some((v) => v?.toLowerCase().includes(t))) &&
      (!filtroAdaptador || i.adaptadores.some((a) => a.tipo === filtroAdaptador)) &&
      (!filtroEstado || i.estado === filtroEstado) &&
      (!filtroSolucion || i.solucion === filtroSolucion)
    );
  }, [iflows, texto, filtroAdaptador, filtroEstado, filtroSolucion]);

  const esPersonal = orgActual?.type === 'personal';
  const abierto = abiertoId ? iflows.find((i) => i.id === abiertoId) : undefined;
  // Espejo de puedeGestionar en functions/services/iflowService.js.
  const puedeGestionarIflow = useCallback(
    (i: Iflow) => esPersonal || i.autorUid === user?.uid || orgActual?.role === 'owner' || orgActual?.role === 'admin',
    [esPersonal, user?.uid, orgActual?.role]
  );
  // A qué iFlows se puede adjuntar el PNG de un boceto (gestionables y con cupo de documentos).
  const iflowsAdjuntables = useMemo(
    () => (archivada ? [] : iflows.filter((i) => puedeGestionarIflow(i) && (i.documentos?.length || 0) < MAX_DOCUMENTOS).map((i) => ({ id: i.id, titulo: i.titulo }))),
    [iflows, archivada, puedeGestionarIflow]
  );
  const selectCls = 'bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50';

  return (
    <main className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto mb-6">
        <Breadcrumbs currentPage="btp-iflows" setPage={setPage} />
      </div>

      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-600 to-cyan-500 shadow-lg shadow-blue-900/40 flex-shrink-0">
              <FolderGit2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-light text-white">Repositorio de iFlows</h1>
              <p className="text-sm text-gray-400 mt-1">Guarda, documenta y reutiliza los iFlows exportados de Integration Suite.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 self-start md:self-auto">
            {orgActual && !sinAcceso && (
              <button onClick={() => setPerfilesAbierto(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-gray-200 hover:border-cyan-500/40 hover:text-white text-sm font-medium"
                title="Datos de cada cliente (hosts, alias, Location ID, filtros…) para reutilizar al adaptar iFlows">
                <KeyRound className="w-4 h-4" /> Perfiles de valores
              </button>
            )}
            {orgActual && !sinAcceso && !archivada && pestana === 'iflows' && (
              <button onClick={() => setSubiendo(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium">
                <Plus className="w-4 h-4" /> Subir iFlow
              </button>
            )}
          </div>
        </header>

        {orgActual && (
          <div className="flex items-start gap-2 bg-cyan-500/10 border border-cyan-500/20 text-cyan-200 p-3 rounded-lg text-xs">
            {esPersonal ? <User className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <Users className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            <span>
              Estás viendo los iFlows de <strong className="text-white">{orgActual.name}</strong>
              {esPersonal ? ' (tu repositorio personal: solo tú los ves).' : ' (compartidos con los miembros de la organización).'}
              {' '}Para ver otro repositorio, cambia la organización en el selector de arriba.
            </span>
          </div>
        )}
        {archivada && (
          <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 p-3 rounded-lg text-xs">
            <Archive className="w-4 h-4" /> Esta organización está archivada: puedes ver y descargar, pero no subir ni editar.
          </div>
        )}
        {aviso && (
          <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 p-3 rounded-lg text-xs">
            <Info className="w-4 h-4" /> {aviso}
          </div>
        )}
        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

        {orgsLoading ? null : !orgActual ? (
          <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl text-sm">Elige una organización en el selector de arriba.</div>
        ) : sinAcceso ? (
          <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl text-sm">
            Como invitado de esta organización no tienes acceso a su repositorio de iFlows.
          </div>
        ) : (
          <>
            <nav className="flex gap-1 border-b border-white/10 text-sm">
              {([['iflows', `iFlows (${iflows.length})`], ['disenos', 'Diseños']] as ['iflows' | 'disenos', string][]).map(([p, label]) => (
                <button key={p} onClick={() => setPestana(p)}
                  className={`px-3 py-2 -mb-px border-b-2 ${pestana === p ? 'border-cyan-400 text-cyan-300' : 'border-transparent text-gray-400 hover:text-white'}`}>
                  {label}
                </button>
              ))}
            </nav>

            {pestana === 'disenos' ? (
              <DisenosRepositorio
                orgId={orgActual.id}
                archivada={archivada}
                iflowsAdjuntables={iflowsAdjuntables}
                abiertoId={disenoAbiertoId}
                onAbrir={setDisenoAbiertoId}
                onAviso={mostrarAviso}
              />
            ) : (
            <>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Buscar por título, descripción, ID técnico, sistema o etiqueta…"
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                />
              </div>
              <select className={selectCls} value={filtroAdaptador} onChange={(e) => setFiltroAdaptador(e.target.value)} aria-label="Filtrar por adaptador">
                <option value="">Todos los adaptadores</option>
                {adaptadores.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select className={selectCls} value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} aria-label="Filtrar por estado">
                <option value="">Todos los estados</option>
                {ESTADOS_IFLOW.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
              {soluciones.length > 0 && (
                <select className={selectCls} value={filtroSolucion} onChange={(e) => setFiltroSolucion(e.target.value)} aria-label="Filtrar por solución">
                  <option value="">Todas las soluciones</option>
                  {soluciones.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </div>

            {cargando ? (
              <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
                <RefreshCw className="w-6 h-6 animate-spin text-cyan-500" />
                <p className="text-sm">Cargando iFlows…</p>
              </div>
            ) : iflows.length === 0 ? (
              <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
                <FolderGit2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                <p className="text-sm">Todavía no hay iFlows en este repositorio.</p>
                {!archivada && <p className="text-xs mt-1">Exporta un iFlow desde Integration Suite (.zip) y súbelo con "Subir iFlow".</p>}
              </div>
            ) : filtrados.length === 0 ? (
              <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl text-sm">Ningún iFlow coincide con la búsqueda.</div>
            ) : (
              <>
                <p className="text-xs text-gray-500">{filtrados.length} de {iflows.length} iFlow{iflows.length === 1 ? '' : 's'}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtrados.map((i) => (
                    <IflowCard
                      key={i.id}
                      iflow={i}
                      onAbrir={() => setAbiertoId(i.id)}
                      onDescargar={() => descargarIflow(i.orgId, i.id).catch((err) => setError(err.message))}
                    />
                  ))}
                </div>
              </>
            )}
            </>
            )}
          </>
        )}
      </div>

      {perfilesAbierto && orgActual && (
        <PerfilesValoresModal orgId={orgActual.id} soloLectura={archivada} onCerrar={() => setPerfilesAbierto(false)} />
      )}

      {subiendo && orgActual && (
        <SubirIflowModal
          orgId={orgActual.id}
          orgNombre={orgActual.name}
          onCerrar={() => setSubiendo(false)}
          onCreado={() => {
            setSubiendo(false);
            mostrarAviso('iFlow guardado.');
            cargar();
          }}
        />
      )}

      {abierto && orgActual && (
        <DetalleIflowModal
          iflow={abierto}
          esPersonal={esPersonal}
          puedeGestionar={puedeGestionarIflow(abierto)}
          soloLectura={archivada}
          onCerrar={() => setAbiertoId(null)}
          onCambio={cargar}
          onBorrado={() => {
            setAbiertoId(null);
            mostrarAviso('iFlow borrado.');
            cargar();
          }}
          onAviso={mostrarAviso}
          onCrearDiseno={(id) => {
            setAbiertoId(null);
            setPestana('disenos');
            setDisenoAbiertoId(id);
          }}
        />
      )}
    </main>
  );
}
